import React, { useEffect, useState } from "react";
import {
  IoIosArrowRoundBack,
  IoIosArrowRoundForward,
  IoIosPersonAdd,
} from "react-icons/io";
import { Link } from "react-router-dom";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import InterbranchDetails from "./InterbranchDetails";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ExportData from "../../components/common/ExportData";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import { ChevronLeft, ChevronRight, Plus, Eye, Check, X } from "lucide-react";
import { showApiErrors } from "../../utils/helpers";

const InterbranchTransfers = () => {
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transferList, setTransferList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [activeTab, setActiveTab] = useState("outgoing"); // "all", "outgoing", "incoming"

  const fetchTransferList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/interbranch?page=${current_page}&pageSize=${page_size}&search=${search_query}`,
        "GET"
      );
      setTransferList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/interbranch?page=${current_page}&pageSize=10000000000000000000000&search=${search_query}`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteInterbranch = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to delete this transfer?",
      showDenyButton: true,
      confirmButtonText: "Yes Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Interbranch transfer deleted successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const approveInterbranch = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to approve this transfer?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        const body = JSON.stringify({
          status: "Approved",
        });
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/?action=approve`,
            "PUT",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Approved!",
            text: "Interbranch transfer approved successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          showApiErrors(error);
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const acceptInterbranch = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to accept this transfer?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        const body = JSON.stringify({
          status: "Approved",
        });
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/?action=accept`,
            "PUT",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Accepted!",
            text: "Interbranch transfer accepted successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          showApiErrors(error);
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const filterTransferList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: "bg-yellow-100 text-yellow-800",
      Approved: "bg-green-100 text-green-800",
      Rejected: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-3 py-1 rounded-lg text-xs font-semibold ${statusConfig[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  // Filter transfers based on active tab
  const getFilteredTransfers = () => {
    if (!transferList || !activeSession?.branch?.name) return transferList;

    switch (activeTab) {
      case "outgoing":
        return transferList.filter(
          (item) => item.branch === activeSession.branch.name
        );
      case "incoming":
        return transferList.filter(
          (item) => item.destination_branch === activeSession.branch.name
        );
      default:
        return transferList;
    }
  };

  const filteredTransfers = getFilteredTransfers();

  useEffect(() => {
    fetchTransferList(pageSize, search, currentPage);
    getTableData(pageSize, search, currentPage);
  }, [search, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {modalOpen && selectedTransfer && (
          <InterbranchDetails
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            transfer={selectedTransfer}
          />
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Transfer</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Branch</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Interbranch Transfers
              </h1>
            </div>
            <RoleBasedComponent allowedRoles={["dispenser", "branch_manager"]}>
              <Link
                to="/inventory/interbranch/create"
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Plus size={20} />
                Create Branch Transfer
              </Link>
            </RoleBasedComponent>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab("outgoing")}
                className={`px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === "outgoing"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Outgoing
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {transferList?.filter(
                    (item) => item.branch === activeSession?.branch?.name
                  ).length || 0}
                </span>
              </button>
              {/* <button
                onClick={() => setActiveTab("incoming")}
                className={`px-6 py-4 text-sm font-medium transition-colors relative ${
                  activeTab === "incoming"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Incoming
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {transferList?.filter(
                    (item) =>
                      item.destination_branch === activeSession?.branch?.name
                  ).length || 0}
                </span>
              </button> */}
            </div>
          </div>

          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Pending Interbranch Transfer"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Filter:</span>
                <input
                  type="text"
                  placeholder="Type to filter..."
                  onChange={(e) => filterTransferList(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white w-64"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <RotatingLines
                    visible={true}
                    height="24"
                    width="24"
                    strokeColor="#3B82F6"
                    strokeWidth="5"
                    animationDuration="0.75"
                  />
                  <span>Loading transfers...</span>
                </div>
              </div>
            ) : filteredTransfers?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Source Branch
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Destination Branch
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Items
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTransfers?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.destination_branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.total_cost}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.total_items}
                      </td>
                      <td className="py-4 px-6 text-sm">
                        {getStatusBadge(item?.status)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.created_at}
                      </td>

                      {activeSession?.user?.role !== "dispenser" ? (
                        <>
                          {updating ? (
                            <td className="py-4 px-6">
                              <RotatingLines
                                height="20"
                                width="20"
                                strokeColor="#3B82F6"
                              />
                            </td>
                          ) : (
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => approveInterbranch(item?.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                  <Check size={14} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => deleteInterbranch(item?.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  <X size={14} />
                                  Remove
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedTransfer(item);
                                    setModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                  <Eye size={14} />
                                  View
                                </button>
                              </div>
                            </td>
                          )}
                        </>
                      ) : (
                        <>
                          {updating ? (
                            <td className="py-4 px-6">
                              <RotatingLines
                                height="20"
                                width="20"
                                strokeColor="#3B82F6"
                              />
                            </td>
                          ) : (
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedTransfer(item);
                                    setModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                  <Eye size={14} />
                                  View
                                </button>
                                {item?.status === "Approved" &&
                                  activeSession?.branch?.name ==
                                  item?.destination_branch && (
                                    <button
                                      onClick={() => acceptInterbranch(item.id)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                    >
                                      <Check size={14} />
                                      Accept
                                    </button>
                                  )}
                              </div>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">
                  {activeTab === "all"
                    ? "No transfers found"
                    : activeTab === "outgoing"
                      ? "No outgoing transfers found"
                      : "No incoming transfers found"}
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredTransfers?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterbranchTransfers;
